import {computed, inject, Injectable, linkedSignal, resource, signal} from '@angular/core';
import {Observable} from 'rxjs';
import {take, tap} from 'rxjs/operators';
import {cloneDeep as _cloneDeep, isEqual as _isEqual} from 'lodash';
import {debouncedSignal} from '../../utils/signals/debounced-signal';
import {HttpService} from '../technique/http.service';
import {GraphQLService} from '../technique/graphql.service';
import {ResponseWrapper} from '../../suppliers/wrappers/response-wrapper';
import {PlcProduitComplementaireDTO} from '../../dtos/plc-produit-complementaire-dto';
import {
  PeriodeConditionnementDTO
} from '../../dtos/produit/declinaison/complementaire/periode-conditionnement-dto';
import {
  PlcProduitComplementaireChanges
} from '../../models/gestion-produits/declinaison/complementaire/plc/plc-produit-complementaire-changes';
import {JourToggle} from '../../models/gestion-produits/declinaison/complementaire/plc/jour-toggle';
import {
  buildPageCount,
  PREMIERE_PAGE,
  SEARCH_DEBOUNCE_MS,
  sliceForPage,
  URL_POST_SAVE_PRODUITS_COMPLEMENTAIRES
} from './plc-produit-complementaire.service';
import {ProduitComplementaireParametrageService} from './produit-complementaire-parametrage.service';

/**
 * Critères de la grille déclenchant une requête réseau. `produitId` à `null` : rien à charger.
 * La pagination n'en fait pas partie, elle se fait côté client sur la liste complète.
 */
interface FiltresGrille {
  produitId: number | null;
  libelle: string;
}

/** Filtres du catalogue des points de livraison associables. `produitDeclinaisonId` à `null` : rien à charger. */
interface FiltresPlcAssociable {
  produitDeclinaisonId: number | null;
  libelle: string;
}

const FILTRES_GRILLE_VIDE: FiltresGrille = {produitId: null, libelle: ''};

const FILTRES_PLC_VIDE: FiltresPlcAssociable = {produitDeclinaisonId: null, libelle: ''};

/**
 * Paramétrage des produits complémentaires vu de la fiche produit. Une ligne vaut une association
 * (point de livraison, produit décliné) : c'est ce couple qui identifie les modifications en attente.
 */
@Injectable({
  providedIn: 'root'
})
export class ProduitProduitComplementaireService {

  private readonly httpSvc = inject(HttpService);
  private readonly graphQlSvc = inject(GraphQLService);
  private readonly paramSvc = inject(ProduitComplementaireParametrageService);

  private readonly gridFiltresSignal = signal<FiltresGrille>(FILTRES_GRILLE_VIDE);
  private readonly plcAssociableFiltresSignal = signal<FiltresPlcAssociable>(FILTRES_PLC_VIDE);

  private readonly gridFiltresAppelSignal = debouncedSignal(this.gridFiltresSignal, SEARCH_DEBOUNCE_MS, _isEqual);
  private readonly plcAssociableFiltresAppelSignal =
    debouncedSignal(this.plcAssociableFiltresSignal, SEARCH_DEBOUNCE_MS, _isEqual);

  private readonly gridPageAskedSignal = signal<number>(PREMIERE_PAGE);
  private readonly plcAssociablePageAskedSignal = signal<number>(PREMIERE_PAGE);

  private readonly editedByKeyMap = signal<Map<string, PlcProduitComplementaireDTO>>(new Map());
  private readonly deletedByKeyMap = signal<Map<string, PlcProduitComplementaireDTO>>(new Map());

  readonly hasChangesSignal = computed(() => !!this.editedByKeyMap().size || !!this.deletedByKeyMap().size);

  private readonly produitComplementaireResource = resource({
    params: this.gridFiltresAppelSignal,
    defaultValue: [] as PlcProduitComplementaireDTO[],
    loader: async ({params}) => params.produitId == null
      ? []
      : this.fetchParametrageList(params.produitId, params.libelle)
  });

  /** Lignes du serveur amputées des suppressions en attente : c'est ce que la grille pagine. */
  private readonly visibleListSignal = computed<PlcProduitComplementaireDTO[]>(() => {
    const deletedByKeyMap = this.deletedByKeyMap();
    return (this.produitComplementaireResource.value() ?? [])
      .filter(row => !deletedByKeyMap.has(PlcProduitComplementaireChanges.buildKey(row)));
  });

  readonly gridTotalSignal = computed(() => this.visibleListSignal().length);

  private readonly gridPageCount = computed(() => buildPageCount(this.gridTotalSignal()));

  /** Une suppression ou une recherche peut vider la dernière page : on ne sort jamais des bornes. */
  readonly gridPageSignal = computed(() => Math.min(this.gridPageAskedSignal(), this.gridPageCount()));

  /** Ligne serveur de la page courante, avant saisies en attente : base de comparaison pour `collectPageEdits`. */
  private readonly pageSnapshotSignal = computed<PlcProduitComplementaireDTO[]>(() =>
    sliceForPage(this.visibleListSignal(), this.gridPageSignal()));

  /**
   * Reste modifiable — la grille écrit dedans — mais repart du serveur dès que la page, la liste ou les saisies
   * en attente changent. Chaque ligne est une copie, y compris sans saisie en attente : DevExtreme modifie la
   * ligne en place (case Facturable, Commandable portail), et une ligne partagée avec `pageSnapshotSignal`
   * modifierait aussi la référence de `collectPageEdits`, qui ne verrait alors aucune différence.
   */
  readonly currentPageList = linkedSignal<{ snapshotList: PlcProduitComplementaireDTO[]; editedByKeyMap: Map<string, PlcProduitComplementaireDTO> },
    PlcProduitComplementaireDTO[]>({
    source: () => ({snapshotList: this.pageSnapshotSignal(), editedByKeyMap: this.editedByKeyMap()}),
    computation: ({snapshotList, editedByKeyMap}) =>
      snapshotList.map(row => _cloneDeep(editedByKeyMap.get(PlcProduitComplementaireChanges.buildKey(row)) ?? row))
  });

  initProduit = (produitId: number | null): void => {
    this.gridFiltresSignal.set({...FILTRES_GRILLE_VIDE, produitId});
    this.plcAssociableFiltresSignal.set(FILTRES_PLC_VIDE);
    this.gridPageAskedSignal.set(PREMIERE_PAGE);
    // Vide aussi les saisies désormais abandonnées : `currentPageList` repart du serveur.
    this.resetPendingChanges();
  };

  private resetPendingChanges = (): void => {
    this.editedByKeyMap.set(new Map());
    this.deletedByKeyMap.set(new Map());
  };

  goToPage = (page: number): void => {
    this.collectPageEdits();
    this.gridPageAskedSignal.set(page);
  };

  searchGrid = (libelle: string): void => {
    this.collectPageEdits();
    this.gridPageAskedSignal.set(PREMIERE_PAGE);
    this.gridFiltresSignal.update(filtres => ({...filtres, libelle: libelle.trim()}));
  };

  private fetchParametrageList = async (produitId: number, libelle: string): Promise<PlcProduitComplementaireDTO[]> => {
    const parts: string[] = [`produitId: ${produitId}`];
    if (libelle) {
      parts.push(`libelle: "${this.graphQlSvc.escapeQueryValue(libelle)}"`);
    }

    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allPlcProduitComplementaires(filters: { ${parts.join(', ')} }) {
          ${this.paramSvc.PLC_PRODUIT_COMPLEMENTAIRE_PARAMETRAGE}
        }
      }
    `).pipe(take(1)).toPromise();

    return ((response?.allPlcProduitComplementaires ?? []) as any[]).map(this.paramSvc.mapLigne);
  };

  /** Reporte les saisies de la page courante dans les modifications en attente. */
  collectPageEdits = (): void => {
    const snapshotByKeyMap = new Map(this.pageSnapshotSignal().map(row => [PlcProduitComplementaireChanges.buildKey(row), row]));
    const editedByKeyMap = new Map(this.editedByKeyMap());

    this.currentPageList().forEach(row => {
      const key = PlcProduitComplementaireChanges.buildKey(row);
      const snapshot = snapshotByKeyMap.get(key);
      if (!snapshot) {
        return;
      }

      if (PlcProduitComplementaireChanges.isSame(snapshot, row)) {
        editedByKeyMap.delete(key);
        return;
      }
      editedByKeyMap.set(key, _cloneDeep(row));
    });
    this.editedByKeyMap.set(editedByKeyMap);
  };

  toggleJourConsommation = (jour: JourToggle): void => {
    const key = PlcProduitComplementaireChanges.buildKey(jour);
    this.currentPageList.update(list => list.map(row => PlcProduitComplementaireChanges.buildKey(row) !== key
      ? row
      : this.paramSvc.toggleJourConsommationLigne(row, jour.jourId)));
    this.collectPageEdits();
  };

  applyPeriodeConditionnementList = (produitComplementaire: PlcProduitComplementaireDTO,
                                     periodeList: PeriodeConditionnementDTO[]): void => {
    const key = PlcProduitComplementaireChanges.buildKey(produitComplementaire);
    this.currentPageList.update(list => list.map(row => PlcProduitComplementaireChanges.buildKey(row) !== key
      ? row
      : {...row, periodeConditionnementList: periodeList}));
    this.collectPageEdits();
  };

  /** La ligne disparaît de la liste visible, donc du total et de la page, par le seul jeu des signaux dérivés. */
  markDeleted = (produitComplementaire: PlcProduitComplementaireDTO): void => {
    this.deletedByKeyMap.update(current =>
      new Map(current).set(PlcProduitComplementaireChanges.buildKey(produitComplementaire), _cloneDeep(produitComplementaire)));
  };

  buildChanges = (): PlcProduitComplementaireChanges => {
    this.collectPageEdits();
    const deletedByKeyMap = this.deletedByKeyMap();
    return PlcProduitComplementaireChanges.buildWithEditAndDeleteList(
      [...this.editedByKeyMap().entries()].filter(([key]) => !deletedByKeyMap.has(key)).map(([, row]) => row),
      [...deletedByKeyMap.values()]);
  };

  saveChanges = (changes: PlcProduitComplementaireChanges): Observable<ResponseWrapper<PlcProduitComplementaireDTO>> =>
    this.httpSvc.post(URL_POST_SAVE_PRODUITS_COMPLEMENTAIRES, changes)
        .pipe(tap((response: ResponseWrapper<PlcProduitComplementaireDTO>) => {
          this.applySavedRowList(response?.resultList ?? []);
          this.resetPendingChanges();
        }));

  /**
   * Un ajout crée des lignes qui n'existent pas dans la liste en mémoire : seule une relecture peut les faire
   * apparaître au bon rang. `.reload()` reste donc justifié ici, à la différence de `saveChanges` qui ne modifie
   * que des lignes déjà chargées.
   */
  addProduits = (produitToAddList: PlcProduitComplementaireDTO[]): Observable<ResponseWrapper<PlcProduitComplementaireDTO>> =>
    this.httpSvc.post(URL_POST_SAVE_PRODUITS_COMPLEMENTAIRES,
                      PlcProduitComplementaireChanges.buildWithProduitToAddList(produitToAddList))
        .pipe(tap(() => {
          this.collectPageEdits();
          this.produitComplementaireResource.reload();
        }));

  /**
   * Aligne la liste serveur sur ce que la sauvegarde a produit : les lignes supprimées la quittent, les lignes
   * acceptées y sont fusionnées. Sans ça, vider les modifications en attente ferait réapparaître les suppressions
   * et repasserait la page aux valeurs d'avant sauvegarde.
   *
   * <p>La réponse porte les lignes acceptées, pas une relecture de la base : c'est la fusion qui les normalise
   * comme la base les stockera.</p>
   */
  private applySavedRowList = (savedList: PlcProduitComplementaireDTO[]): void => {
    const deletedByKeyMap = this.deletedByKeyMap();
    const savedByKeyMap = new Map(savedList.map(row => [PlcProduitComplementaireChanges.buildKey(row), row]));

    this.produitComplementaireResource.update(list => (list ?? [])
      .filter(row => !deletedByKeyMap.has(PlcProduitComplementaireChanges.buildKey(row)))
      .map(row => {
        const saved = savedByKeyMap.get(PlcProduitComplementaireChanges.buildKey(row));
        return saved ? PlcProduitComplementaireChanges.mergeParametrage(row, _cloneDeep(saved)) : row;
      }));
  };

  private readonly plcAssociableResource = resource({
    params: this.plcAssociableFiltresAppelSignal,
    defaultValue: [] as PlcProduitComplementaireDTO[],
    loader: async ({params}) => params.produitDeclinaisonId == null
      ? []
      : this.fetchPlcAssociableList(params.produitDeclinaisonId, params.libelle)
  });

  readonly plcAssociableTotalSignal = computed(() => (this.plcAssociableResource.value() ?? []).length);

  private readonly plcAssociablePageCount = computed(() => buildPageCount(this.plcAssociableTotalSignal()));

  readonly plcAssociablePageSignal = computed(() =>
    Math.min(this.plcAssociablePageAskedSignal(), this.plcAssociablePageCount()));

  readonly plcAssociableList = computed<PlcProduitComplementaireDTO[]>(() =>
    sliceForPage(this.plcAssociableResource.value() ?? [], this.plcAssociablePageSignal()));

  private fetchPlcAssociableList = async (produitDeclinaisonId: number,
                                          libelle: string): Promise<PlcProduitComplementaireDTO[]> => {
    const parts: string[] = [`produitDeclinaisonId: ${produitDeclinaisonId}`];
    if (libelle) {
      parts.push(`libelle: "${this.graphQlSvc.escapeQueryValue(libelle)}"`);
    }

    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allPlcAssociables(filters: { ${parts.join(', ')} }) {
          ${this.paramSvc.PLC_PRODUIT_COMPLEMENTAIRE_PARAMETRAGE}
        }
      }
    `).pipe(take(1)).toPromise();

    return ((response?.allPlcAssociables ?? []) as any[]).map(this.paramSvc.mapLigne);
  };

  searchPlcAssociable = (libelle: string): void => {
    this.plcAssociablePageAskedSignal.set(PREMIERE_PAGE);
    this.plcAssociableFiltresSignal.update(filtres => ({...filtres, libelle: libelle.trim()}));
  };

  goToPlcAssociablePage = (page: number): void => this.plcAssociablePageAskedSignal.set(page);

  /** Chargement des points de livraison associables au produit décliné choisi, filtres remis à zéro. */
  openPlcAssociable = (produitDeclinaisonId: number): void => {
    this.plcAssociablePageAskedSignal.set(PREMIERE_PAGE);
    this.plcAssociableFiltresSignal.set({...FILTRES_PLC_VIDE, produitDeclinaisonId});
  };

  /** Fermeture : plus de produit décliné dans les filtres, donc plus rien de chargé. */
  closePlcAssociable = (): void => this.plcAssociableFiltresSignal.set(FILTRES_PLC_VIDE);
}
